// src/pages/AdminChatsPage.tsx
import React, { useState, useEffect } from 'react';
import { Card, List, Avatar, Typography, Empty, Space, Badge } from 'antd';
import { UserOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useAuth } from '../App';
import { chatAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const { Title, Text } = Typography;

interface Conversation {
  id: number;
  patient_id: number;
  doctor_id: number;
  is_support?: boolean;
  created_at: string;
  updated_at: string;
  unread_count?: number;
  patient_full_name?: string | null;
  patient?: {
    id: number;
    full_name: string;
    email: string;
  };
  last_message?: {
    id: number;
    encrypted_content: string;
    created_at: string;
  };
}

export const AdminChatsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadConversations();
    }
  }, [user]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getConversations();
      // Filter only support conversations for admin
      const allConvs = (response.data || []) as Conversation[];
      const supportConvs = allConvs.filter((c: Conversation) => c.is_support);
      setConversations(supportConvs);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationClick = (conversationId: number) => {
    navigate(`/admin/chats/${conversationId}`);
  };
  
  // Refresh conversations periodically to update unread counts
  useEffect(() => {
    const interval = setInterval(() => {
      if (user?.role === 'admin') {
        loadConversations();
      }
    }, 10000); // Refresh every 10 seconds
    
    return () => clearInterval(interval);
  }, [user]);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#263238' }}>Чаты поддержки</Title>
        <Text type="secondary">Беседы пользователей со службой поддержки</Text>
      </div>

      <Card style={{ borderRadius: '12px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">Загрузка...</Text>
          </div>
        ) : conversations.length === 0 ? (
          <Empty
            description="Нет бесед с поддержкой"
            image={<CustomerServiceOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          />
        ) : (
          <List
            dataSource={conversations}
            renderItem={(conversation) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: '16px',
                  borderBottom: '1px solid #f0f0f0',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => handleConversationClick(conversation.id)}
                role="button"
                tabIndex={0}
                aria-label={`Открыть чат поддержки ${conversation.patient_full_name || conversation.patient?.full_name || conversation.patient_id}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleConversationClick(conversation.id);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <List.Item.Meta
                  avatar={
                    <Badge count={conversation.unread_count || 0} offset={[-5, 5]}>
                      <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#7CB342' }} />
                    </Badge>
                  }
                  title={
                    <Space>
                      <Text strong>
                        {conversation.patient_full_name
                          || conversation.patient?.full_name
                          || `Пользователь #${conversation.patient_id}`}
                      </Text>
                      {conversation.unread_count && conversation.unread_count > 0 && (
                        <Badge count={conversation.unread_count} style={{ backgroundColor: '#7CB342' }} />
                      )}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={4}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Последнее сообщение: {conversation.last_message 
                          ? format(new Date(conversation.last_message.created_at), 'dd.MM.yyyy HH:mm')
                          : 'Нет сообщений'}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};
